/**
 * NewAssessmentChooser — /assess/new (Phase 3, mock).
 *
 * Two-step new-assessment wizard, ported from verbal-preview AssessHubPage:
 *   Step 1 — population: 1-to-1 or Group (Group reveals a "devices available"
 *            toggle that switches between live device check-in and proxy read).
 *   Step 2 — scale type: pick from the scale catalog. Selecting routes onward:
 *            1-to-1  → /assess/take/:scaleId  (take the survey)
 *            Group + devices → /assess/group-live/<assessmentId>
 *            Group + no devices → /assess/group-proxy
 *
 * A 1-to-1 run also picks a SESSION (PR #5 review, Sean). The session is what tells the server
 * whether this is a start-of-programme or an end-of-programme measurement — the first session is
 * the baseline, the last is the endpoint — so it is required rather than optional. There is
 * deliberately no Baseline/Endpoint dropdown here: it would be a second answer to a question the
 * session has already answered, and the two would eventually disagree.
 *
 * FRONTEND-ONLY — choices held in AssessFlowContext.
 * TODO[VER-108/Phase3]: persist the chosen population + scale to a real
 *   assessment-run record instead of in-memory flow state.
 * TODO[RBAC]: gate at the route level with the AssessView permit once defined.
 */
import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Grid,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { GroupOutlined, PersonOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Participant, Session } from "common/build/prisma/client";
import { useMobile } from "../../../hooks/useMobile";
import { VPill, VPillAmber } from "../../../core/components/VPill";
import { list as getParticipants } from "../../../client/api/participants";
import { listSurveyTemplates, PersistedSurveyTemplate } from "../../../client/api/survey";
import * as sessionApi from "../../../client/api/session";
import { SCALE_CATALOG, ScaleCatalogEntry, ScaleTag } from "./scaleCatalog";
import { AssessPopulation, useAssessFlow } from "./AssessFlowContext";
import { isAssessPreviewEnabled } from "../../../config/featureFlags";

/**
 * A participant plus the programmes they are enrolled on. Response capture needs BOTH ids —
 * the endpoint rejects a participant who is not enrolled on the programme — so the programme
 * is derived from the enrolment rather than asked for separately (VER-114).
 */
type ParticipantWithProgrammes = Participant & {
    ParticipantToProgramme?: {
        Programme: { id: number; name: string; isGroup: boolean };
        /** Consent is held per enrolment, so it travels with the join row, not the participant. */
        hasOptedOutOfSurveys: boolean;
    }[];
};

// Mock assessment id used when launching the group live flow from a fresh wizard.
const NEW_GROUP_ASSESSMENT_ID = "mock-new-group";

/**
 * An enrolment option's value is `"<participantId>:<programmeId>"` (see `enrolments` below).
 * Returns the programme, or null when nothing is selected.
 */
const programmeIdFromEnrolmentKey = (key: string): number | null => {
    const programmeId = Number(key.split(":")[1]);
    return Number.isInteger(programmeId) && programmeId > 0 ? programmeId : null;
};

// ---------------------------------------------------------------------------
// Step 1 — population card
// ---------------------------------------------------------------------------

type PopulationCardProps = {
    value: AssessPopulation;
    title: string;
    description: string;
    icon: React.ReactNode;
    selected: boolean;
    onSelect: (value: AssessPopulation) => void;
};

const PopulationCard = ({ value, title, description, icon, selected, onSelect }: PopulationCardProps): JSX.Element => (
    <Card
        onClick={() => onSelect(value)}
        sx={{
            cursor: "pointer",
            height: "100%",
            transition: "box-shadow 0.15s, border-color 0.15s",
            border: "2px solid",
            borderColor: selected ? "primary.main" : "borderShade.main",
            "&:hover": {
                boxShadow: (theme) => `0 2px 12px ${theme.palette.secondary.main}22`,
            },
        }}
    >
        <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                {icon}
                <Typography variant="h6" component="h3">
                    {title}
                </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
                {description}
            </Typography>
        </CardContent>
    </Card>
);

// ---------------------------------------------------------------------------
// Step 2 — scale card
// ---------------------------------------------------------------------------

const tagPill = (tag: ScaleTag): JSX.Element => {
    if (tag === "Recommended") {
        return <VPill key={tag} size="small" label="Recommended" color="primary" />;
    }
    return <VPillAmber key={tag} size="small" label="Clinical" />;
};

type ScaleCardProps = {
    scale: ScaleCatalogEntry;
    disabled: boolean;
    onSelect: (scale: ScaleCatalogEntry) => void;
};

const ScaleCard = ({ scale, disabled, onSelect }: ScaleCardProps): JSX.Element => {
    const isRecommended = scale.tags.includes("Recommended");
    const isClinical = scale.tags.includes("Clinical");

    return (
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column", opacity: disabled ? 0.55 : 1 }}>
            <CardContent sx={{ flex: 1 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                    <Typography variant="h6" component="h3" sx={{ mr: "auto" }}>
                        {scale.name}
                    </Typography>
                    {scale.tags.map((tag) => tagPill(tag))}
                </Box>

                <Typography variant="body2" color="textSecondary" mb={1.5}>
                    {scale.summary}
                </Typography>

                <Typography variant="body2" color="textPrimary" mb={isRecommended || isClinical ? 1 : 0}>
                    {scale.itemCount} items · Recommended for {scale.recommendedFor.join(", ")}
                </Typography>

                {isRecommended ? (
                    <Typography variant="caption" color="success.dark" display="block">
                        Best fit for the chosen population.
                    </Typography>
                ) : null}
                {isClinical ? (
                    <Typography variant="caption" color="warning.dark" display="block">
                        Clinical scale — surface only when escalation is warranted.
                    </Typography>
                ) : null}
            </CardContent>
            <CardContent sx={{ pt: 0 }}>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={disabled}
                    onClick={() => onSelect(scale)}
                >
                    Start assessment
                </Button>
            </CardContent>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const NewAssessmentChooser = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;
    const navigate = useNavigate();
    const { state, choosePopulation, setDevicesAvailable, chooseScale, chooseSubject } = useAssessFlow();

    const population = state.population;

    const [participants, setParticipants] = useState<ParticipantWithProgrammes[]>([]);
    const [templates, setTemplates] = useState<PersistedSurveyTemplate[]>([]);
    const [selectedEnrolment, setSelectedEnrolment] = useState<string>("");
    const [sessions, setSessions] = useState<Session[]>([]);
    const [sessionId, setSessionId] = useState<number | "">("");
    const [loadError, setLoadError] = useState<string | null>(null);

    // 1-to-1 capture needs a real participant + programme + published template. Loaded once;
    // the group paths (VER-115/VER-116) still run on their own screens.
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            getParticipants({ myProgrammesOnly: true, includeProgrammes: true, take: 500 }),
            listSurveyTemplates(),
        ])
            .then(([participantPage, templateList]) => {
                if (cancelled) return;
                setParticipants(participantPage.items as ParticipantWithProgrammes[]);
                setTemplates(templateList.filter((t) => t.status === "Published"));
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "Participants and surveys could not be loaded.");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    /**
     * One option per enrolment — a participant on two programmes is two distinct choices.
     *
     * Consent is per ENROLMENT, not per person: a participant may have withdrawn on one programme
     * and not another, so the opt-out is filtered here rather than over the participant list. The
     * API refuses the write as well; dropping the option keeps a practitioner from being offered
     * something that will be rejected.
     */
    const enrolments = participants.flatMap((p) =>
        (p.ParticipantToProgramme ?? [])
            .filter((ptp) => !ptp.Programme.isGroup && !ptp.hasOptedOutOfSurveys)
            .map((ptp) => ({
                key: `${p.id}:${ptp.Programme.id}`,
                label: `${p.name} — ${ptp.Programme.name}`,
                participantId: p.id,
                programmeId: ptp.Programme.id,
            })),
    );

    /**
     * Sessions belong to a programme, so the list reloads whenever the chosen enrolment changes and
     * any previously picked session is cleared — the capture endpoint rejects a session from a
     * different programme, and a stale one would be rejected on submit instead of here.
     */
    useEffect(() => {
        setSessionId("");
        // The programme is read out of the selection key rather than by searching `enrolments`:
        // that array is rebuilt on every render, so depending on it would refetch the session list
        // each time. The key is the only value that actually changes.
        const selectedProgrammeId = programmeIdFromEnrolmentKey(selectedEnrolment);
        if (selectedProgrammeId === null) {
            setSessions([]);
            return;
        }
        let cancelled = false;
        sessionApi
            .list(selectedProgrammeId)
            .then((list) => {
                if (!cancelled) setSessions(list);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "Sessions could not be loaded.");
            });
        return () => {
            cancelled = true;
        };
    }, [selectedEnrolment]);

    const orderedSessions = [...sessions].sort((a, b) => a.sessionNumber - b.sessionNumber);
    const lastSessionNumber = orderedSessions.length ? orderedSessions[orderedSessions.length - 1].sessionNumber : null;

    /**
     * What the chosen session means, shown next to the picker. The SERVER decides this — the label
     * only mirrors the same first-versus-last rule so the practitioner is not asked to trust an
     * invisible one.
     */
    const sessionMeaning = (sessionNumber: number): string => {
        if (sessionNumber <= 1) return "start-of-programme measurement";
        if (lastSessionNumber !== null && sessionNumber >= lastSessionNumber) {
            return "end-of-programme measurement";
        }
        return "mid-programme measurement";
    };

    const handleSelectScale = (scale: ScaleCatalogEntry) => {
        if (!population) {
            return;
        }
        chooseScale(scale.id);

        if (population === "group") {
            // The group-live screen is a preview surface, so its route only exists when the flag is
            // on. Navigating there regardless fell through to the /assess/* splat and silently
            // re-rendered this page — the practitioner picked a scale and landed back where they
            // started with no explanation. With the flag off, proxy entry is the real path.
            const groupPath =
                state.devicesAvailable && isAssessPreviewEnabled()
                    ? `/assess/group-live/${NEW_GROUP_ASSESSMENT_ID}`
                    : "/assess/group-proxy";
            navigate(groupPath);
            return;
        }
        // 1-to-1 → take the survey through the builder engine.
        navigate(`/assess/take/${scale.id}`);
    };

    /** Start a real, persisted 1-to-1 assessment against a published template. */
    const handleStartPersisted = (template: PersistedSurveyTemplate) => {
        const enrolment = enrolments.find((e) => e.key === selectedEnrolment);
        // The session is required, not optional: it is what the server reads the measurement point
        // from, so starting without one would only produce a 400 after the survey had been answered.
        if (!enrolment || sessionId === "") {
            return;
        }
        chooseSubject({
            participantId: enrolment.participantId,
            programmeId: enrolment.programmeId,
            sessionId,
        });
        navigate(`/assess/take/${template.id}`);
    };

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile && <Box pt={8} />}

            {/* Header */}
            <Box mb={4} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Typography variant="h2" color="textPrimary">
                    New assessment
                </Typography>
                <Button variant="outlined" color="primary" onClick={() => navigate("/assess")}>
                    Cancel
                </Button>
            </Box>

            {/* Step 1 — population */}
            <Box mb={4}>
                <Typography variant="h5" color="textPrimary" mb={2}>
                    1. Who is this assessment for?
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <PopulationCard
                            value="1-2-1"
                            title="1-to-1"
                            description="A single participant on a 1-2-1 pathway (e.g. social work referral)."
                            icon={<PersonOutlined color="primary" />}
                            selected={population === "1-2-1"}
                            onSelect={choosePopulation}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <PopulationCard
                            value="group"
                            title="Group"
                            description="A group or class completing the assessment together (e.g. a school cohort)."
                            icon={<GroupOutlined color="primary" />}
                            selected={population === "group"}
                            onSelect={choosePopulation}
                        />
                    </Grid>
                </Grid>

                {population === "group" ? (
                    <Box mt={2}>
                        <Tooltip title="When off, the facilitator records the assessment on the group's behalf (proxy read).">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={state.devicesAvailable}
                                        onChange={(e) => setDevicesAvailable(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={state.devicesAvailable ? "Devices available" : "Proxy read (no devices)"}
                            />
                        </Tooltip>
                    </Box>
                ) : null}
            </Box>

            {/* Step 2 (1-to-1) — participant + published survey. This is the path that persists. */}
            {population === "1-2-1" ? (
                <Box mb={4}>
                    <Typography variant="h5" color="textPrimary" mb={2}>
                        2. Which participant?
                    </Typography>

                    {loadError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {loadError}
                        </Alert>
                    ) : null}

                    <TextField
                        select
                        fullWidth
                        label="Participant and programme"
                        value={selectedEnrolment}
                        onChange={(e) => setSelectedEnrolment(e.target.value)}
                        helperText={
                            enrolments.length === 0
                                ? "No 1-to-1 enrolments found on your programmes."
                                : "The assessment is recorded against this participant on this programme."
                        }
                        disabled={enrolments.length === 0}
                        sx={{ maxWidth: 520, mb: 3 }}
                    >
                        {enrolments.map((e) => (
                            <MenuItem key={e.key} value={e.key}>
                                {e.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Typography variant="h5" color="textPrimary" mb={2}>
                        3. Which session?
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        label="Session"
                        value={sessionId}
                        onChange={(event) => setSessionId(Number(event.target.value))}
                        helperText={
                            !selectedEnrolment
                                ? "Choose a participant first."
                                : orderedSessions.length === 0
                                  ? "This programme has no sessions yet, so an assessment cannot be recorded against it."
                                  : "The session decides whether this is recorded as a start-, mid- or end-of-programme measurement."
                        }
                        disabled={!selectedEnrolment || orderedSessions.length === 0}
                        sx={{ maxWidth: 520, mb: 3 }}
                    >
                        {orderedSessions.map((session) => (
                            <MenuItem key={session.id} value={session.id}>
                                {`Session ${session.sessionNumber} — ${sessionMeaning(session.sessionNumber)}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Typography variant="h5" color="textPrimary" mb={1}>
                        4. Choose a survey
                    </Typography>
                    {templates.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            No published surveys yet — publish one in the survey builder first.
                        </Typography>
                    ) : null}
                    <Grid container spacing={2}>
                        {templates.map((template) => (
                            <Grid item xs={12} sm={6} md={4} key={template.id}>
                                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                    <CardContent sx={{ flex: 1 }}>
                                        <Typography variant="h6" component="h3" mb={1}>
                                            {template.title}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {template.instructions}
                                        </Typography>
                                    </CardContent>
                                    <CardContent sx={{ pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            disabled={!selectedEnrolment || sessionId === ""}
                                            onClick={() => handleStartPersisted(template)}
                                        >
                                            Start assessment
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : null}

            {/* Demo scale catalog — group paths, and 1-to-1 preview before any survey is published. */}
            <Box>
                <Typography variant="h5" color="textPrimary" mb={1}>
                    {population === "1-2-1" ? "Or preview a demo scale (not saved)" : "2. Choose a scale"}
                </Typography>
                {!population ? (
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Choose a population first to enable the scales below.
                    </Typography>
                ) : null}
                <Grid container spacing={2}>
                    {SCALE_CATALOG.map((scale) => (
                        <Grid item xs={12} sm={6} md={4} key={scale.id}>
                            <ScaleCard scale={scale} disabled={!population} onSelect={handleSelectScale} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};

export default NewAssessmentChooser;
